import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Spinner } from 'reactstrap';
import { fetchItems } from "../../../services/Http/Api";
import { useNavigate } from 'react-router-dom';
import SearchFilters from "./SearchFilters";
import { useTranslation } from 'react-i18next';
import { MdVisibility } from 'react-icons/md';
import styled from 'styled-components';

const ItemsOverviewContainer = styled.div`
    padding: 1.25rem;
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;

    h4 {
        color: rgba(130, 22, 146, 1);
        font-weight: bold;
    }
`;

const ActionButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: rgba(130, 22, 146, 1);

    &:hover {
        color: rgba(130, 22, 146, 0.8);
    }
`;

const StyledSpinner = styled(Spinner)`
    color: rgba(130, 22, 146, 1);
`;

const ItemsOverview = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [filters, setFilters] = useState({ searchTerm: '', manufacturerId: '' });
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('');

    const handleViewItem = (id) => {
        navigate(`/items/view/${id}`);
    };

    const columns = [
        {
            name: t('Items.Overview.serial_number'),
            selector: row => row.serial_number || "N/A",
            sortable: true,
            key: 'serial_number'
        },
        {
            name: t('Items.Overview.product_name'),
            selector: row => row.product_name || "N/A",
            sortable: true,
            key: 'product_name'
        },
        {
            name: t('Items.Overview.model_number'),
            selector: row => row.model_number || "N/A",
            sortable: true,
            key: 'model_number'
        },
        {
            name: t('Items.Overview.manufacturer_name'),
            selector: row => row.manufacturer_name || "N/A",
            sortable: true,
            key: 'manufacturer_name'
        },
        {
            name: t('Items.Overview.variant_sku'),
            selector: row => row.variant_sku || "N/A",
            sortable: true,
            key: 'variant_sku'
        },
        {
            name: t('Items.Overview.organization_sku'),
            selector: row => row.organization_sku || "N/A",
            sortable: true,
            key: 'organization_sku'
        },
        {
            name: t('Items.Overview.status'),
            selector: row => row.is_current_holder ? t('Items.Overview.status_in_stock') : t('Items.Overview.status_sold'),
        },
        {
            name: t('Items.Overview.actions'),
            cell: row => (
                <ActionButton onClick={() => handleViewItem(row.id)}>
                    <MdVisibility size={20} />
                </ActionButton>
            ),
        },
    ];

    const fetchData = async (page, size = perPage, appliedFilters = filters) => {
        setLoading(true);
        try {
            const response = await fetchItems({
                page,
                perPage: size,
                sortBy: sortField,
                sortDirection,
                search: appliedFilters.searchTerm,
                manufacturerId: appliedFilters.manufacturerId,
            });

            const { data, total } = response;
            setItems(data);
            setTotalRows(total);
        } catch (error) {
            console.error("Error fetching items:", error);
        } finally {
            setLoading(false);
        }
    };

    // Isto kao na katalogu: prazna tablica koja ceka pretragu izgleda kao da
    // trgovac nema nijedan artikl.
    useEffect(() => {
        fetchData(1);
    }, []);

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1);
        fetchData(1, perPage, newFilters);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchData(page);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        fetchData(page, newPerPage);
    };

    const handleSort = (column, sortDirection) => {
        setSortField(column.key);
        setSortDirection(sortDirection);
        fetchData(currentPage);
    };

    return (
        <ItemsOverviewContainer>
            <Header>
                <h4>{t('Items.Overview.title')}</h4>
            </Header>

            <SearchFilters onFilter={handleFilterChange} />

            <DataTable
                columns={columns}
                data={items}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                onSort={handleSort}
                sortServer
                highlightOnHover
                progressPending={loading}
                progressComponent={<StyledSpinner />}
            />
        </ItemsOverviewContainer>
    );
};

export default ItemsOverview;
